import React, { useState, useEffect } from 'react';

const ManageRoles = () => {
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data for users and permissions
    // In a real application, this would come from an API call
    const mockUsers = [
      {
        id: 1,
        name: 'Admin User',
        email: 'admin@edumanage.edu',
        role: 'admin',
        permissions: ['read', 'write', 'delete', 'manage_users', 'manage_settings']
      },
      {
        id: 2,
        name: 'Teacher User',
        email: 'teacher@edumanage.edu',
        role: 'teacher',
        permissions: ['read', 'write', 'manage_attendance', 'manage_marks']
      },
      {
        id: 3,
        name: 'Student User',
        email: 'student@edumanage.edu',
        role: 'student',
        permissions: ['read']
      }
    ];

    const mockPermissions = {
      admin: {
        name: 'Administrator',
        description: 'Full access to all system features',
        permissions: ['read', 'write', 'delete', 'manage_users', 'manage_settings', 'manage_results', 'manage_admissions', 'manage_gallery', 'manage_notices']
      },
      teacher: {
        name: 'Teacher',
        description: 'Access to teaching and grading features',
        permissions: ['read', 'write', 'manage_attendance', 'manage_marks', 'upload_materials', 'view_students']
      },
      student: {
        name: 'Student',
        description: 'Access to personal academic information',
        permissions: ['read', 'view_profile', 'view_results', 'view_attendance', 'download_materials']
      }
    };

    setTimeout(() => {
      setUsers(mockUsers);
      setPermissions(mockPermissions);
      setLoading(false);
    }, 1000); // Simulate API call delay
  }, []);

  const updateUserRole = (userId, newRole) => {
    setUsers(users.map(user => 
      user.id === userId ? { ...user, role: newRole } : user
    ));
  };

  const updateRolePermissions = (role, newPermissions) => {
    setPermissions({
      ...permissions,
      [role]: {
        ...permissions[role],
        permissions: newPermissions
      }
    });
  };

  const availablePermissions = [
    { id: 'read', name: 'Read Access', description: 'Can view data' },
    { id: 'write', name: 'Write Access', description: 'Can create/edit data' },
    { id: 'delete', name: 'Delete Access', description: 'Can delete data' },
    { id: 'manage_users', name: 'Manage Users', description: 'Can manage user accounts' },
    { id: 'manage_settings', name: 'Manage Settings', description: 'Can manage system settings' },
    { id: 'manage_results', name: 'Manage Results', description: 'Can manage academic results' },
    { id: 'manage_admissions', name: 'Manage Admissions', description: 'Can manage admission applications' },
    { id: 'manage_gallery', name: 'Manage Gallery', description: 'Can manage gallery content' },
    { id: 'manage_notices', name: 'Manage Notices', description: 'Can manage notices' },
    { id: 'manage_attendance', name: 'Manage Attendance', description: 'Can manage attendance records' },
    { id: 'manage_marks', name: 'Manage Marks', description: 'Can manage student marks' },
    { id: 'upload_materials', name: 'Upload Materials', description: 'Can upload study materials' },
    { id: 'view_students', name: 'View Students', description: 'Can view student information' },
    { id: 'view_profile', name: 'View Profile', description: 'Can view own profile' },
    { id: 'view_results', name: 'View Results', description: 'Can view academic results' },
    { id: 'view_attendance', name: 'View Attendance', description: 'Can view attendance records' },
    { id: 'download_materials', name: 'Download Materials', description: 'Can download study materials' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Manage Roles & Permissions</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">User Roles</h2>
              <div className="space-y-4">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 border rounded">
                    <div>
                      <div className="font-medium text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                    <select
                      value={user.role}
                      onChange={(e) => updateUserRole(user.id, e.target.value)}
                      className="border rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="admin">Admin</option>
                      <option value="teacher">Teacher</option>
                      <option value="student">Student</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Permission Summary</h2>
              <div className="space-y-4">
                {Object.entries(permissions).map(([role, roleData]) => (
                  <div key={role} className="border rounded p-4">
                    <h3 className="font-semibold text-gray-800 capitalize">{roleData.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">{roleData.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {roleData.permissions.map(perm => {
                        const permInfo = availablePermissions.find(p => p.id === perm);
                        return (
                          <span key={perm} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                            {permInfo?.name || perm}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {Object.entries(permissions).map(([role, roleData]) => (
              <div key={role} className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 capitalize">{roleData.name} Permissions</h2>
                <p className="text-gray-600 mb-4">{roleData.description}</p>
                
                <div className="space-y-3">
                  {availablePermissions.map(permission => (
                    <div key={permission.id} className="flex items-start">
                      <input
                        type="checkbox"
                        id={`${role}-${permission.id}`}
                        checked={roleData.permissions.includes(permission.id)}
                        onChange={(e) => {
                          const newPermissions = e.target.checked
                            ? [...roleData.permissions, permission.id]
                            : roleData.permissions.filter(p => p !== permission.id);
                          updateRolePermissions(role, newPermissions);
                        }}
                        className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor={`${role}-${permission.id}`} className="ml-2 block">
                        <span className="font-medium text-gray-700">{permission.name}</span>
                        <span className="block text-sm text-gray-500">{permission.description}</span>
                      </label>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      {roleData.permissions.length} of {availablePermissions.length} permissions granted
                    </span>
                    <button className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition text-sm">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Role Guidelines</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-gray-800">Admin Role</h3>
              <p className="text-sm text-gray-600 mt-1">Should be assigned only to trusted personnel with administrative responsibilities.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-800">Teacher Role</h3>
              <p className="text-sm text-gray-600 mt-1">For faculty members who need to manage academic records and student information.</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-gray-800">Student Role</h3>
              <p className="text-sm text-gray-600 mt-1">For students to access their academic information and resources.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageRoles;